import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Camera, Loader2, Moon, Sun } from "lucide-react";
import { toast } from "sonner";
import { base44 as db } from "@/api/base44Client";
import { CANTONS } from "@/lib/subjects";
import useCurrentUser from "@/lib/useCurrentUser";
import { useAuth } from "@/lib/AuthContext";
import { Image as UiImage } from "@/components/ui/image";
import ReferralPanel from "@/components/ReferralPanel";
import LotteryPanel from "@/components/lottery/LotteryPanel";

const LABEL = "text-[10px] font-bold uppercase tracking-widest text-black";
const INPUT = "w-full mt-0.5 border-2 border-black rounded-lg px-2.5 py-1.5 text-sm font-bold bg-white text-black";

export default function Profile() {
  const { user } = useCurrentUser();
  const { refreshUser } = useAuth();
  const qc = useQueryClient();
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "light");
  const [form, setForm] = useState({
    full_name: user?.full_name || user?.name || "",
    age: user?.age ?? "",
    canton: user?.canton || "",
    ort: user?.ort || "",
    bio: user?.bio || "",
    invisible: user?.invisible || false,
    profile_picture: user?.profile_picture || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const toggleTheme = () => {
    const nextTheme = theme === "light" ? "dark" : "light";
    setTheme(nextTheme);
    localStorage.setItem("theme", nextTheme);
    if (nextTheme === "dark") {
      document.documentElement.classList.add("dark");
      document.documentElement.setAttribute("data-theme","dark");
      document.querySelector(".App")?.setAttribute("data-theme","dark");
    } else {
      document.documentElement.classList.remove("dark");
      document.documentElement.setAttribute("data-theme","light");
      document.querySelector(".App")?.setAttribute("data-theme","light");
    }
  };

  useEffect(() => {
    if (user) setForm({
      full_name: user.full_name || user.name || "",
      age: user.age ?? "",
      canton: user.canton || "",
      ort: user.ort || "",
      bio: user.bio || "",
      invisible: user.invisible || false,
      profile_picture: user.profile_picture || "",
    });
  }, [user?.user_id, user?.name]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const { file_url } = await db.integrations.Core.UploadPublicFile({ file });
      set("profile_picture", file_url);
      toast.success("Profilbild aktualisiert");
    } catch (e) {
      toast.error("Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    let lat = null, lng = null;
    try {
      const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 5000 }));
      lat = pos.coords.latitude;
      lng = pos.coords.longitude;
    } catch(e) {}
    setSaving(true);
    const payload = {
      age: form.age === "" ? null : Number(form.age),
      canton: form.canton,
      ort: form.ort,
      bio: form.bio,
      profile_picture: form.profile_picture,
      full_name: form.full_name,
      invisible: form.invisible,
      lat, lng,
    };
    try {
      await db.auth.updateMe(payload);
      await refreshUser();
      await qc.invalidateQueries();
      toast.success("Profil gespeichert");
    } catch (e) {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">PROFIL</p>
          <h1 className="font-heading text-xl sm:text-3xl font-extrabold text-black dark:text-white">Dein Profil</h1>
        </div>
        <button
          type="button"
          onClick={toggleTheme}
          className="flex items-center gap-1.5 border-2 border-black rounded-lg px-2.5 py-1 bg-white hover:bg-slate-100 transition-all cursor-pointer shadow-sm text-black"
        >
          {theme === "dark" ? <Moon className="w-3.5 h-3.5" /> : <Sun className="w-3.5 h-3.5 text-amber-500" />}
          <span className="text-xs font-bold">{theme === "dark" ? "Dunkel" : "Hell"}</span>
        </button>
      </div>
      <div className="brutal bg-white p-3 mt-2 space-y-2 border-2 border-black rounded-xl">
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-center gap-1 shrink-0">
            <div className="w-16 h-16 border-2 border-black rounded-xl overflow-hidden bg-[#FDFFB6] grid place-items-center">
              {form.profile_picture ? (
                <UiImage src={form.profile_picture} fittingType="cover" className="w-full h-full" />
              ) : (
                <Camera className="w-6 h-6 text-black" />
              )}
            </div>
            <label className="brutal-btn bg-white text-black border border-black rounded px-2 py-0.5 text-[10px] cursor-pointer font-bold">
              {uploading ? "Lädt…" : "Bild ändern"}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} disabled={uploading} />
            </label>
          </div>
          <div className="flex-1 min-w-0">
            <label className={LABEL}>Name</label>
            <input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={INPUT} />
            <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{user?.email}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={LABEL}>Alter</label>
            <input type="number" min="16" max="99" value={form.age} onChange={(e) => set("age", e.target.value)} className={INPUT} />
          </div>
          <div>
            <label className={LABEL}>Kanton</label>
            <select value={form.canton} onChange={(e) => set("canton", e.target.value)} className={INPUT}>
              <option value="">– wählen –</option>
              {CANTONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className={LABEL}>Ort</label>
          <input value={form.ort} onChange={(e) => set("ort", e.target.value)} className={INPUT} />
        </div>
        <div>
          <label className={LABEL}>Bio</label>
          <textarea value={form.bio} onChange={(e) => set("bio", e.target.value)} rows={2} className={`${INPUT} font-semibold`} />
        </div>
      </div>
      <div className="flex items-center justify-between border-2 border-black rounded-lg px-3 py-2 bg-white mt-2 text-black">
        <span className="font-bold text-sm">Unsichtbar (nicht in Leute anzeigen)</span>
        <button
          type="button"
          onClick={() => set("invisible", !form.invisible)}
          className={`w-12 h-6 rounded-full border-2 border-black transition-all ${form.invisible ? "bg-black" : "bg-[#CAFFBF]"}`}
        >
          <div className={`w-4 h-4 bg-white rounded-full border-2 border-black transition-all mx-0.5 ${form.invisible ? "translate-x-6" : ""}`} />
        </button>
      </div>
      <button onClick={save} disabled={saving} className="brutal-btn bg-black text-white w-full py-3 mt-3 font-heading flex items-center justify-center gap-2 rounded-xl">
        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : null} Speichern
      </button>
      <div className="mt-6 border-t-2 border-black pt-6">
        <ReferralPanel />
        <LotteryPanel />
      </div>
    </div>
  );
}
